import React from "react";
import {
  Users,
  Target,
  Globe,
  BookOpen,
  Award,
  Briefcase,
  ArrowRight,
  CheckCircle,
  Mail,
  Phone,
} from "lucide-react";
import Banner from "../../../Components/Common/Banner";

const HandsOnLearn = () => {
  const keyPoints = [
    {
      icon: <Target className="w-8 h-8" />,
      title: "Hands-On Training",
      description:
        "Providing practical, industry-relevant experience to STEM students.",
      color: "bg-blue-50 text-blue-600",
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Comprehensive Mentorship",
      description:
        "Offering guidance and support from experienced professionals.",
      color: "bg-green-50 text-green-600",
    },
    {
      icon: <Globe className="w-8 h-8" />,
      title: "Global Professionalism",
      description:
        "Fostering skills and competencies essential for success in an international context.",
      color: "bg-purple-50 text-purple-600",
    },
    {
      icon: <BookOpen className="w-8 h-8" />,
      title: "Educational Alignment",
      description:
        "Tailoring internships to align with academic goals and career development.",
      color: "bg-orange-50 text-orange-600",
    },
  ];

  const benefits = [
    "Extensive paid fulltime training opportunities",
    "Industry-specific knowledge development",
    "Tailored internships matching career aspirations",
    "Comprehensive skill development programs",
    "Global professional network access",
    "Competitive career preparation",
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Banner
        pageName="HANDS ON LEARN"
        breadcrumbs="Home / Programs / Hands On Learn"
      />

      {/* Hero Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Cultivating Global STEM Professionals
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                At Hands-On Learn, we specialize in offering extensive paid
                fulltime training opportunities to STEM students, aimed at
                cultivating global professionals in their respective fields of
                study. Our program focuses on providing hands-on experience and
                comprehensive mentorship, empowering participants to develop
                practical skills and industry-specific knowledge.
              </p>
              {/* <div className="flex flex-wrap gap-4">
                <button className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center gap-2">
                  Apply Now <ArrowRight className="w-4 h-4" />
                </button>
                <button className="border border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors">
                  Learn More
                </button>
              </div> */}
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-blue-100 to-purple-100 rounded-2xl p-8 h-96 flex items-center justify-center">
                <div className="text-center">
                  <Briefcase className="w-24 h-24 text-blue-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">
                    Professional Training
                  </h3>
                  <p className="text-gray-600">
                    Hands-on experience in STEM fields
                  </p>
                </div>
              </div>
              <div className="absolute -top-4 -right-4 bg-yellow-400 rounded-full p-3">
                <Award className="w-6 h-6 text-yellow-800" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Points Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Program Highlights
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our comprehensive approach ensures students receive the best
              preparation for their professional careers
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {keyPoints.map((point, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className={`${point.color} rounded-lg p-3 w-fit mb-4`}>
                  {point.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {point.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {point.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Statement Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="bg-gradient-to-br from-green-100 to-blue-100 rounded-2xl p-8 h-96 flex items-center justify-center">
              <div className="text-center">
                <Globe className="w-24 h-24 text-green-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  Global Impact
                </h3>
                <p className="text-gray-600">
                  Preparing students for international careers
                </p>
              </div>
            </div>
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Our Commitment
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                We are committed to nurturing talent through tailored
                internships that align with educational objectives and career
                aspirations, preparing students for successful careers in a
                competitive global landscape.
              </p>
              <div className="space-y-3">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold mb-2">500+</div>
              <div className="text-blue-200">Students Trained</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">50+</div>
              <div className="text-blue-200">Industry Partners</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">95%</div>
              <div className="text-blue-200">Job Placement Rate</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">20+</div>
              <div className="text-blue-200">Countries Reached</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Start Your Professional Journey?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Join our comprehensive training program and take the first step
            towards a successful STEM career
          </p>
          <div className="flex gap-6 text-blue-100  items-center justify-center">
            <div className="flex items-center ">
              <Phone className="w-4 h-4 mr-2" />
              <span>[+1 470 740 2777]</span>
            </div>
            <div className="flex items-center">
              <Mail className="w-4 h-4 mr-2" />
              <span>[info@techmentee.com]</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HandsOnLearn;
